/**
 * A community's groupings, on both sides of the page (ADR 0052).
 *
 * THE VIEWER'S SIDE is a filter and a set of headings. Several groupings
 * picked at once are ALTERNATIVES — "Goldens" and "Speedrun" is everybody in
 * either — because two groupings of one community are two answers to the same
 * question, and requiring both would find only the few people who do
 * everything. The Twitch tag and the search box then narrow that set.
 *
 * THE EDITOR'S SIDE is a draft saved whole, like the channel list: rename a
 * grouping, tick people into it, save once.
 *
 * Wire shapes throughout (snake_case where there is any), because that is
 * what the console holds — see communityFilter.ts for why nothing here
 * translates.
 */
import type { Grouping, RoomGroupings, RoomGroupingsInput } from '@tomo/contracts';

/** What the page needs to know about a person to group them. */
export interface Groupable {
  readonly login: string;
  readonly groupings: readonly string[];
}

/** Is this person in any of the picked groupings? Nothing picked is everybody. */
export function matchesGroupings(member: Groupable, picked: ReadonlySet<string>): boolean {
  if (picked.size === 0) return true;
  return member.groupings.some((id) => picked.has(id));
}

/** One heading on the page, and who is under it. */
export interface Heading<T> {
  /** A grouping id, or null for "everyone else". */
  readonly id: string | null;
  readonly label: string;
  readonly blurb: string;
  readonly members: readonly T[];
}

/**
 * The offline list, under the community's headings.
 *
 * SOMEBODY IN TWO GROUPINGS IS UNDER BOTH. Somebody reading only the
 * "Modded goldens" heading should find everybody who does modded goldens; a
 * heading that left out the people already listed further up would not be a
 * list of that grouping at all. Only top-level groupings are headings — a
 * child ("Vanilla" under "Speedrun") is a label on the card, not a heading of
 * its own, or a person in a child would appear twice in a row.
 *
 * SECTIONS ("Community events") come back separately, and whoever is in one
 * is under no other heading: it says they are an organisation, not a
 * streamer who also belongs somewhere else.
 *
 * Order within a heading is the order given, which is the server's.
 */
export function headingsFor<T extends Groupable>(
  members: readonly T[],
  groupings: readonly Grouping[],
): { headings: Heading<T>[]; sections: Heading<T>[] } {
  const sectionIds = new Set(groupings.filter((g) => g.section).map((g) => g.id));
  const inSection = (m: T) => m.groupings.some((id) => sectionIds.has(id));
  const sections = groupings
    .filter((g) => g.section)
    .map((g) => ({ id: g.id, label: g.label, blurb: g.blurb, members: members.filter((m) => m.groupings.includes(g.id)) }))
    .filter((h) => h.members.length > 0);

  const people = members.filter((m) => !inSection(m));
  const tops = groupings.filter((g) => !g.section && g.parent === null);
  const headings: Heading<T>[] = tops
    .map((g) => ({ id: g.id, label: g.label, blurb: g.blurb, members: people.filter((m) => m.groupings.includes(g.id)) }))
    .filter((h) => h.members.length > 0);
  const topIds = new Set(tops.map((g) => g.id));
  const rest = people.filter((m) => !m.groupings.some((id) => topIds.has(id)));
  // Only worth a heading when there are other headings to be "else" to.
  if (rest.length > 0) {
    headings.push({ id: null, label: headings.length > 0 ? 'Everyone else' : 'Everyone', blurb: '', members: rest });
  }
  return { headings, sections };
}

/**
 * The labels a card shows: the child groupings, which headings do not.
 *
 * Named WITH their parent — "Speedrun · Modded", not "Modded". A card sits
 * under whichever heading is being read, and a bare "Modded" under the
 * Modded heading says something the community did not.
 */
export function childLabels(member: Groupable, groupings: readonly Grouping[]): string[] {
  const label = new Map(groupings.map((g) => [g.id, g.label]));
  return groupings
    .filter((g) => g.parent !== null && member.groupings.includes(g.id))
    .map((g) => `${label.get(g.parent ?? '') ?? ''} · ${g.label}`);
}

// ── The editor's draft ─────────────────────────────────────────────────────

export interface GroupingDraft {
  readonly groupings: readonly Grouping[];
  /** The EDITORS' ticks, by login. What a streamer set is in `locked`. */
  readonly ticks: Readonly<Record<string, readonly string[]>>;
  /**
   * What a streamer chose for themselves, by login: `in` groupings the
   * editor cannot untick, `out` ones they cannot tick. Read-only here.
   */
  readonly locked: Readonly<Record<string, { readonly in: readonly string[]; readonly out: readonly string[] }>>;
}

export function draftFrom(saved: RoomGroupings): GroupingDraft {
  const ticks: Record<string, string[]> = {};
  const locked: Record<string, { in: string[]; out: string[] }> = {};
  for (const a of saved.assignments) {
    const editor = a.groupings.filter((g) => g.by === 'editor').map((g) => g.id);
    const own = a.groupings.filter((g) => g.by === 'streamer').map((g) => g.id);
    if (editor.length > 0) ticks[a.login] = editor;
    if (own.length > 0 || a.declined.length > 0) locked[a.login] = { in: own, out: [...a.declined] };
  }
  return { groupings: saved.groupings, ticks, locked };
}

/** What a box shows for this person and grouping. */
export type TickState = 'on' | 'off' | 'theirs-on' | 'theirs-off';

export function tickState(draft: GroupingDraft, login: string, id: string): TickState {
  const lock = draft.locked[login];
  if (lock?.in.includes(id)) return 'theirs-on';
  if (lock?.out.includes(id)) return 'theirs-off';
  return draft.ticks[login]?.includes(id) ? 'on' : 'off';
}

/** Tick or untick. A box the streamer set is left alone: it is theirs. */
export function toggleTick(draft: GroupingDraft, login: string, id: string): GroupingDraft {
  const state = tickState(draft, login, id);
  if (state === 'theirs-on' || state === 'theirs-off') return draft;
  const current = draft.ticks[login] ?? [];
  const next = state === 'on' ? current.filter((g) => g !== id) : [...current, id];
  return { ...draft, ticks: { ...draft.ticks, [login]: next } };
}

/** A key from a label: "Modded goldens" → "modded-goldens", unique within the draft. */
export function keyFor(label: string, taken: ReadonlySet<string>): string {
  const base = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 28)
    .replace(/-+$/g, '') || 'grouping';
  let key = base;
  for (let n = 2; taken.has(key); n += 1) key = `${base}-${String(n)}`;
  return key;
}

export function addGrouping(
  draft: GroupingDraft,
  label: string,
  options: { parent?: string | null; section?: boolean } = {},
): GroupingDraft {
  const trimmed = label.trim();
  if (trimmed.length === 0) return draft;
  const id = keyFor(trimmed, new Set(draft.groupings.map((g) => g.id)));
  const grouping: Grouping = {
    id, label: trimmed, blurb: '', parent: options.parent ?? null, section: options.section ?? false,
  };
  // A child goes after its parent's last child, so the list reads as a tree.
  const groupings = [...draft.groupings];
  if (grouping.parent !== null) {
    let at = groupings.findIndex((g) => g.id === grouping.parent);
    while (at + 1 < groupings.length && groupings[at + 1]?.parent === grouping.parent) at += 1;
    groupings.splice(at + 1, 0, grouping);
  } else {
    groupings.push(grouping);
  }
  return { ...draft, groupings };
}

export function editGrouping(
  draft: GroupingDraft,
  id: string,
  change: Partial<Pick<Grouping, 'label' | 'blurb'>>,
): GroupingDraft {
  return { ...draft, groupings: draft.groupings.map((g) => (g.id === id ? { ...g, ...change } : g)) };
}

/** Remove a grouping, its children, and every tick on them. */
export function removeGrouping(draft: GroupingDraft, id: string): GroupingDraft {
  const gone = new Set([id, ...draft.groupings.filter((g) => g.parent === id).map((g) => g.id)]);
  const ticks: Record<string, string[]> = {};
  for (const [login, ids] of Object.entries(draft.ticks)) ticks[login] = ids.filter((g) => !gone.has(g));
  return { ...draft, groupings: draft.groupings.filter((g) => !gone.has(g.id)), ticks };
}

/**
 * Move a top-level grouping up or down, taking its children with it.
 * Children keep their place under their parent.
 */
export function moveGrouping(draft: GroupingDraft, id: string, by: -1 | 1): GroupingDraft {
  const blocks: Grouping[][] = [];
  for (const g of draft.groupings) {
    if (g.parent === null) blocks.push([g]);
    else blocks[blocks.length - 1]?.push(g);
  }
  const at = blocks.findIndex((b) => b[0]?.id === id);
  const to = at + by;
  if (at < 0 || to < 0 || to >= blocks.length) return draft;
  const moved = [...blocks];
  const [block] = moved.splice(at, 1);
  if (block) moved.splice(to, 0, block);
  return { ...draft, groupings: moved.flat() };
}

/** The PUT body. Ticks for people no longer on the roster are dropped here. */
export function groupingsBody(draft: GroupingDraft, roster: readonly string[]): RoomGroupingsInput {
  const onRoster = new Set(roster);
  const defined = new Set(draft.groupings.map((g) => g.id));
  return {
    groupings: draft.groupings.map((g) => ({ ...g, label: g.label.trim(), blurb: g.blurb.trim() })),
    assignments: Object.entries(draft.ticks)
      .filter(([login]) => onRoster.has(login))
      .map(([login, ids]) => ({ login, groupings: ids.filter((id) => defined.has(id)) }))
      .filter((a) => a.groupings.length > 0),
  };
}

/** Whether the draft differs from what is saved. Order counts: it is the page's order. */
export function groupingsChanged(draft: GroupingDraft, saved: GroupingDraft, roster: readonly string[]): boolean {
  const norm = (d: GroupingDraft) => {
    const body = groupingsBody(d, roster);
    return JSON.stringify({
      g: body.groupings,
      a: body.assignments.map((a) => [a.login, [...a.groupings].sort()]).sort(),
    });
  };
  return norm(draft) !== norm(saved);
}

/**
 * The groupings a person is in, for the chips on their row: theirs and ours,
 * in the community's order. A child is named with its parent.
 */
export function chipsFor(
  draft: GroupingDraft,
  login: string,
): { id: string; label: string; theirs: boolean }[] {
  const label = new Map(draft.groupings.map((g) => [g.id, g.label]));
  return draft.groupings.flatMap((g) => {
    const state = tickState(draft, login, g.id);
    if (state !== 'on' && state !== 'theirs-on') return [];
    const name = g.parent === null ? g.label : `${label.get(g.parent) ?? ''} · ${g.label}`;
    return [{ id: g.id, label: name, theirs: state === 'theirs-on' }];
  });
}

/**
 * Put several people in one grouping. Anyone already in it, or who took
 * themselves out of it, is left as they are — see `toggleTick`.
 */
export function addPeople(draft: GroupingDraft, id: string, logins: readonly string[]): GroupingDraft {
  let next = draft;
  for (const login of logins) {
    if (tickState(next, login, id) === 'off') next = toggleTick(next, login, id);
  }
  return next;
}

/** Why the draft cannot be saved as it is, or null. Checked before an automatic save. */
export function unsavableBecause(draft: GroupingDraft): string | null {
  const unnamed = draft.groupings.find((g) => g.label.trim().length === 0);
  return unnamed ? 'Every grouping needs a name.' : null;
}
